/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useRef, useState } from 'react';
import { formatCurrency } from '../utils/formatters.ts';
import { DailyBucket } from '../utils/dashboardAggregations.ts';
import './Last30DaysChart.css';

interface Last30DaysChartProps {
  days: DailyBucket[]; // exactly 30, oldest -> newest
  loading?: boolean;
}

const MIN_BAR_HEIGHT = 2;
const MAX_BAR_HEIGHT = 20;

const shortLabel = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

type RGB = [number, number, number];

const hexToRgb = (hex: string): RGB => {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16)
  ];
};

const rgbToHex = ([r, g, b]: RGB): string => {
  const toHex = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const lerpRgb = (from: RGB, to: RGB, t: number): RGB => [
  from[0] + (to[0] - from[0]) * t,
  from[1] + (to[1] - from[1]) * t,
  from[2] + (to[2] - from[2]) * t
];

// Low-to-high gradient in the brand's own palette: a muted slate-navy
// (quiet day - kept lighter than the card's navy background so a short
// bar still reads against it) through brand gold for a middling day, up
// to brand teal for the best day in the window.
const GRADIENT_LOW: RGB = hexToRgb('#3d5166');
const GRADIENT_MID: RGB = hexToRgb('#d9a441');
const GRADIENT_HIGH: RGB = hexToRgb('#45b6c6');

const colorForRatio = (ratio: number): RGB => {
  const clamped = Math.max(0, Math.min(1, ratio));
  return clamped <= 0.5
    ? lerpRgb(GRADIENT_LOW, GRADIENT_MID, clamped / 0.5)
    : lerpRgb(GRADIENT_MID, GRADIENT_HIGH, (clamped - 0.5) / 0.5);
};

// Hover state brightens the same gradient color (mixed toward white)
// rather than swapping to a fixed highlight color, so the highlight
// still makes sense whatever color that bar landed on.
const lighten = (rgb: RGB, amount: number): RGB => lerpRgb(rgb, [255, 255, 255], amount);

const Last30DaysChart: React.FC<Last30DaysChartProps> = ({ days, loading = false }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  // Which kind of pointer started the current press. Mouse users get
  // hover-to-preview; touch/pen users get tap-to-toggle. Keeping the two
  // apart matters because a tap also fires emulated mouse-enter events -
  // with both wired up, a tap would open the details on "hover" and then
  // the click would immediately toggle them closed again (a brief flash).
  const lastPointerType = useRef<string>('mouse');

  if (loading) {
    return (
      <div className="last30-chart">
        <div className="last30-chart__header">
          <span className="last30-chart__title">Last 30 Days</span>
        </div>
        <div className="last30-chart__loading">Loading&hellip;</div>
      </div>
    );
  }

  const maxSales = Math.max(0, ...days.map((d) => d.salesTotal));
  const total = days.reduce((sum, d) => sum + d.salesTotal, 0);

  const heightFor = (sales: number): number => {
    if (maxSales <= 0) return MIN_BAR_HEIGHT;
    const scaled = (sales / maxSales) * MAX_BAR_HEIGHT;
    return Math.max(MIN_BAR_HEIGHT, scaled);
  };

  const hovered = hoverIndex !== null ? days[hoverIndex] : null;

  return (
    <div className="last30-chart">
      <div className="last30-chart__header">
        <span className="last30-chart__title">Last 30 Days</span>
        <span className="last30-chart__total">{formatCurrency(total)}</span>
      </div>

      <div className="last30-chart__tooltip-row" aria-hidden="true">
        {hovered ? (
          <span className="last30-chart__tooltip">
            {shortLabel(hovered.date)} &middot; {formatCurrency(hovered.salesTotal)}
          </span>
        ) : (
          <span className="last30-chart__tooltip last30-chart__tooltip--muted">
            <span className="last30-chart__hint-desktop">Hover over a bar for details</span>
            <span className="last30-chart__hint-mobile">Tap a bar for details</span>
          </span>
        )}
      </div>

      <div className="last30-chart__bars" role="img" aria-label={`Daily sales for the last 30 days, totaling ${formatCurrency(total)}`}>
        {days.map((day, i) => {
          const ratio = maxSales > 0 ? day.salesTotal / maxSales : 0;
          const baseColor = colorForRatio(ratio);
          const barColor = rgbToHex(hoverIndex === i ? lighten(baseColor, 0.25) : baseColor);

          return (
            <div
              key={day.dayKey}
              className="last30-chart__bar-wrap"
              onPointerDown={(e) => {
                lastPointerType.current = e.pointerType;
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') setHoverIndex(i);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setHoverIndex(null);
              }}
              onClick={() => {
                if (lastPointerType.current === 'mouse') return;
                setHoverIndex((current) => (current === i ? null : i));
              }}
            >
              <div
                className="last30-chart__bar"
                style={{ height: `${heightFor(day.salesTotal)}px`, backgroundColor: barColor }}
                title={`${shortLabel(day.date)}: ${formatCurrency(day.salesTotal)}`}
              />
            </div>
          );
        })}
      </div>

      <div className="last30-chart__axis">
        <span>{days.length > 0 ? shortLabel(days[0].date) : ''}</span>
        <span>{days.length > 0 ? shortLabel(days[days.length - 1].date) : ''}</span>
      </div>

      <table className="sr-only">
        <caption>Daily sales for the last 30 days</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Sales</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.dayKey}>
              <td>{shortLabel(day.date)}</td>
              <td>{formatCurrency(day.salesTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Last30DaysChart;
